import React from 'react';

const inputClass = 'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none transition focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100';

const SuperAdminCoupons = ({
  coupons,
  packages,
  form,
  setForm,
  isSaving,
  onSave,
  onEdit,
  onReset,
}) => {
  const activePackages = packages.filter((pkg) => (pkg.status || 'active') === 'active');

  return (
    <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-[420px_1fr]">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{form.id ? 'Edit coupon' : 'Create coupon'}</h3>
            <p className="mt-1 text-sm text-slate-500">Create public or private discount codes for selected plans.</p>
          </div>
          {form.id && (
            <button type="button" onClick={onReset} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">
              New
            </button>
          )}
        </div>

        <div className="mt-5 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Code</label>
              <input value={form.coupon_code} onChange={(e) => setForm((prev) => ({ ...prev, coupon_code: e.target.value.toUpperCase() }))} placeholder="SUMMER50" className={inputClass} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Title</label>
              <input value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} placeholder="Summer offer" className={inputClass} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Visibility</label>
              <select value={form.visibility} onChange={(e) => setForm((prev) => ({ ...prev, visibility: e.target.value }))} className={inputClass}>
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Status</label>
              <select value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))} className={inputClass}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Discount</label>
              <select value={form.discount_type} onChange={(e) => setForm((prev) => ({ ...prev, discount_type: e.target.value }))} className={inputClass}>
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed amount</option>
                <option value="free">Free plan</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Value</label>
              <input
                type="number"
                min="0"
                value={form.discount_value}
                disabled={form.discount_type === 'free'}
                onChange={(e) => setForm((prev) => ({ ...prev, discount_value: e.target.value }))}
                placeholder="50"
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Package scope</label>
              <select value={form.package_scope} onChange={(e) => setForm((prev) => ({ ...prev, package_scope: e.target.value }))} className={inputClass}>
                <option value="all">All packages</option>
                <option value="package">Specific package</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Package</label>
              <select
                value={form.package_id}
                disabled={form.package_scope === 'all'}
                onChange={(e) => setForm((prev) => ({ ...prev, package_id: e.target.value }))}
                className={inputClass}
              >
                <option value="">Select package</option>
                {activePackages.map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>{pkg.package_name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Start date</label>
              <input type="date" value={form.start_date} onChange={(e) => setForm((prev) => ({ ...prev, start_date: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">End date</label>
              <input type="date" value={form.end_date} onChange={(e) => setForm((prev) => ({ ...prev, end_date: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Usage limit</label>
              <input type="number" min="0" value={form.usage_limit} onChange={(e) => setForm((prev) => ({ ...prev, usage_limit: e.target.value }))} placeholder="0 = unlimited" className={inputClass} />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Description</label>
            <textarea value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} rows={3} className={inputClass} placeholder="Optional internal note" />
          </div>

          <button
            type="button"
            onClick={onSave}
            disabled={isSaving}
            className="w-full rounded-xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
          >
            {isSaving ? 'Saving...' : form.id ? 'Update coupon' : 'Create coupon'}
          </button>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Coupons</h3>
            <p className="mt-1 text-sm text-slate-500">Track validity, usage, visibility, and assigned package.</p>
          </div>
          <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{coupons.length} total</div>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Discount</th>
                <th className="px-4 py-3">Package</th>
                <th className="px-4 py-3">Usage</th>
                <th className="px-4 py-3">Validity</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {coupons.map((coupon) => (
                <tr key={coupon.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{coupon.coupon_code}</div>
                    <div className="text-xs text-slate-500">{coupon.title}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-wide text-slate-400">{coupon.visibility}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {coupon.discount_type === 'free'
                      ? '100% free'
                      : coupon.discount_type === 'fixed'
                        ? `Rs. ${Number(coupon.discount_value || 0).toLocaleString('en-IN')}`
                        : `${Number(coupon.discount_value || 0).toLocaleString('en-IN')}%`}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{coupon.package_scope === 'all' ? 'All packages' : coupon.package_name || '-'}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {coupon.used_count || 0} / {Number(coupon.usage_limit || 0) > 0 ? coupon.usage_limit : 'Unlimited'}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    <div>{coupon.start_date || 'Any start'}</div>
                    <div className="text-xs text-slate-400">to {coupon.end_date || 'No expiry'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${coupon.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {coupon.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => onEdit(coupon)} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
              {coupons.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-4 py-10 text-center text-slate-500">No coupons created yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default SuperAdminCoupons;
